<script lang="ts" setup>
import { useDisplay } from 'vuetify'
import { useI18n } from 'vue-i18n'
import { DateTime } from 'luxon'
import { computed, onMounted, onUnmounted, ref, toRef } from 'vue'
import { appConfig } from 'config/app'
import AbuseReportModal from '@/components/landingPage/AbuseReportModal.vue'
import { useCookieConsent } from '@/composables/useCookieConsent'
import { docsUrl } from '@/utils/docsUrl'
import { COMPANY_PHONE } from '@/constants'

const { resetConsent } = useCookieConsent()
const showAbuseModal = ref(false)

const { t, locale } = useI18n()
// Plain anchor, not router-link: see the note in @/utils/docsUrl.
const docsHref = computed(() => docsUrl(locale.value))
const { mobile, smAndUp, height: displayHeight } = useDisplay()
const height = toRef(displayHeight)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const tablet = computed(() => mobile.value && smAndUp.value)

const navHeight = ref(0)
const contentHeight = ref(0)
const footerHeight = ref(0)

const currentYear = DateTime.now().year

const footerComp = ref<any | null>(null)
let footerEl: HTMLElement | null = null
let ro: ResizeObserver | null = null

const measureOnce = (el: Element | null | undefined, assign: (h: number) => void) => {
  if (!el) return
  try {
    const rect = (el as HTMLElement).getBoundingClientRect?.()
    if (rect && typeof rect.height === 'number') {
      assign(Math.round(rect.height))
    }
  } catch {
    // no-op
  }
}

onMounted(() => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  const header = document.getElementsByTagName('header')[0] ?? null
  const content = document.getElementById('content') ?? null
  footerEl = (footerComp.value && (footerComp.value.$el as HTMLElement)) || null

  measureOnce(header, (h) => (navHeight.value = h))
  measureOnce(content, (h) => (contentHeight.value = h))
  measureOnce(footerEl, (h) => (footerHeight.value = h))

  if (typeof (window as any).ResizeObserver !== 'function') return

  ro = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const h = Math.round(entry.contentRect?.height ?? 0)
      const target = entry.target as HTMLElement
      if (!target) continue
      if (header && target === header) navHeight.value = h
      else if (content && target === content) contentHeight.value = h
      else if (footerEl && target === footerEl) footerHeight.value = h
    }
  })

  if (header) ro.observe(header)
  if (content) ro.observe(content)
  if (footerEl) ro.observe(footerEl)
})

onUnmounted(() => {
  if (ro) {
    try {
      ro.disconnect()
    } catch {
      // ignore
    }
    ro = null
  }
})

const supportEmailHref = computed(() => appConfig.urls.supportEmail || 'mailto:service@itsh.dev')
const supportEmailLabel = computed(() => {
  const href = supportEmailHref.value
  if (!href) return t('landing.footer.contact')
  const m = href.match(/^mailto:(.+)$/i)
  return m?.[1] ?? t('landing.footer.contact')
})

const isFooterApp = computed(() => {
  const totalHeight = navHeight.value + contentHeight.value + footerHeight.value
  return totalHeight < height.value
})

const hasSocials = computed(() =>
  appConfig.urls.twitter || appConfig.urls.linkedin || appConfig.urls.github
)
</script>

<template>
  <footer
    ref="footerComp"
    :data-app="isFooterApp"
    class="site-footer"
  >
    <!-- Decorative top border -->
    <div class="footer-border" aria-hidden="true"></div>

    <div class="footer-inner">
      <v-container max-width="1200">
        <!-- Main footer grid -->
        <div class="footer-grid">
          <!-- Brand column -->
          <div class="footer-brand">
            <div class="brand-logo">
              <v-icon icon="mdi-cloud" size="28" />
              <span class="brand-name">ITSH</span>
            </div>
            <p class="brand-tagline">
              {{ $t('landing.footer.tagline') }}
            </p>

            <!-- Contact info -->
            <div class="contact-items">
              <a class="contact-item" :href="`tel:${COMPANY_PHONE.e164}`">
                <v-icon icon="mdi-phone" size="18" />
                <span>{{ COMPANY_PHONE.display }}</span>
              </a>
              <a :href="supportEmailHref" class="contact-item">
                <v-icon icon="mdi-email" size="18" />
                <span>{{ supportEmailLabel }}</span>
              </a>
            </div>
          </div>

          <!-- Products column -->
          <div class="footer-col">
            <h3 class="col-title">{{ $t('landing.footer.products') }}</h3>
            <nav class="col-links">
              <router-link :to="{ name: 'kubernetes' }">Kubernetes</router-link>
              <router-link :to="{ name: 'vserver' }">VServer</router-link>
              <router-link :to="{ name: 'webhosting' }">Webhosting</router-link>
              <router-link :to="{ name: 'domains' }">Domains</router-link>
              <router-link :to="{ name: 'domainsTlds' }">{{ $t('landing.footer.tldOverview') }}</router-link>
              <router-link :to="{ name: 'productsHome' }">{{ $t('landing.footer.allProducts') }}</router-link>
              <router-link :to="{ name: 'pricingCalculator' }">{{ $t('landing.footer.pricingCalculator') }}</router-link>
            </nav>
          </div>

          <!-- Support column -->
          <div class="footer-col">
            <h3 class="col-title">{{ $t('landing.footer.support') }}</h3>
            <nav class="col-links">
              <a
                :href="appConfig.urls.status"
                rel="noopener noreferrer"
                target="_blank"
              >
                {{ $t('landing.footer.status') }}
                <v-icon icon="mdi-open-in-new" size="12" />
              </a>
              <a :href="docsHref">{{ $t('landing.footer.docs') }}</a>
              <router-link :to="{ name: 'blogIndex' }">Blog</router-link>
              <a :href="supportEmailHref">{{ $t('landing.footer.contact') }}</a>
            </nav>
          </div>

          <!-- Legal column -->
          <div class="footer-col">
            <h3 class="col-title">{{ $t('landing.footer.legal') }}</h3>
            <nav class="col-links">
              <router-link :to="{ name: 'impress' }">{{ $t('landing.footer.imprint') }}</router-link>
              <router-link :to="{ name: 'privacy' }">{{ $t('landing.footer.privacy') }}</router-link>
              <router-link :to="{ name: 'agb' }">{{ $t('landing.footer.terms') }}</router-link>
              <router-link :to="{ name: 'nis2' }">{{ $t('landing.footer.nis2') }}</router-link>
              <router-link :to="{ name: 'sla' }">{{ $t('landing.footer.sla') }}</router-link>
              <router-link :to="{ name: 'withdrawal' }">{{ $t('landing.footer.withdrawal') }}</router-link>
              <router-link :to="{ name: 'cancellation' }">{{ $t('landing.footer.cancellation') }}</router-link>
            </nav>
          </div>
        </div>

        <!-- Bottom bar -->
        <div class="footer-bottom">
          <div class="copyright">
            {{ $t('landing.footer.copyright', { year: currentYear }) }}
          </div>

          <!-- Secondary links -->
          <div class="secondary-links">
            <a href="#" @click.prevent="resetConsent">{{ $t('landing.footer.cookieSettings') }}</a>
            <span class="separator">·</span>
            <a href="#" @click.prevent="showAbuseModal = true">{{ $t('landing.footer.reportAbuse') }}</a>
            <span class="separator">·</span>
            <a href="/llms.txt">llms.txt</a>
          </div>

          <!-- Social links -->
          <div v-if="hasSocials" class="social-links">
            <a
              v-if="appConfig.urls.github"
              :href="appConfig.urls.github"
              class="social-link"
              rel="noopener"
              target="_blank"
              aria-label="GitHub"
            >
              <v-icon icon="mdi-github" size="20" />
            </a>
            <a
              v-if="appConfig.urls.linkedin"
              :href="appConfig.urls.linkedin"
              class="social-link"
              rel="noopener"
              target="_blank"
              aria-label="LinkedIn"
            >
              <v-icon icon="mdi-linkedin" size="20" />
            </a>
            <a
              v-if="appConfig.urls.twitter"
              :href="appConfig.urls.twitter"
              class="social-link"
              rel="noopener"
              target="_blank"
              aria-label="Twitter"
            >
              <v-icon icon="mdi-twitter" size="20" />
            </a>
          </div>

          <div class="legal-note">
            {{ $t('landing.footer.pricesIncludeTax') }}
          </div>
        </div>
      </v-container>
    </div>

    <!-- Abuse Report Modal -->
    <AbuseReportModal v-model="showAbuseModal" />
  </footer>
</template>

<style lang="scss" scoped>
.site-footer {
  position: relative;
  background: linear-gradient(180deg, rgba(8, 14, 30, 0.95), rgba(5, 10, 22, 0.98));
  color: rgba(255, 255, 255, 0.85);
}

.footer-border {
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(0, 176, 255, 0.3),
    rgba(31, 226, 212, 0.3),
    rgba(0, 176, 255, 0.3),
    transparent
  );
}

.footer-inner {
  padding: 60px 24px 32px;
}

/* Main grid layout */
.footer-grid {
  display: grid;
  grid-template-columns: 1.5fr repeat(3, 1fr);
  gap: 48px;
  margin-bottom: 48px;
}

/* Brand column */
.footer-brand {
  padding-right: 24px;
}

.brand-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;

  .v-icon {
    color: #00B0FF;
  }
}

.brand-name {
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #fff, rgba(200, 218, 255, 0.9));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.brand-tagline {
  font-size: 0.9rem;
  line-height: 1.6;
  color: rgba(200, 218, 255, 0.85);
  margin-bottom: 24px;
  max-width: 280px;
}

.contact-items {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.contact-item {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.2s ease;

  .v-icon {
    color: rgba(0, 176, 255, 0.7);
  }

  &:hover {
    color: #00B0FF;
  }
}

/* Link columns */
.footer-col {
  display: flex;
  flex-direction: column;
}

.col-title {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 20px;
  position: relative;
  padding-bottom: 12px;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 24px;
    height: 2px;
    background: linear-gradient(90deg, #00B0FF, transparent);
    border-radius: 1px;
  }
}

.col-links {
  display: flex;
  flex-direction: column;
  gap: 12px;

  a {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: rgba(200, 218, 255, 0.85);
    text-decoration: none;
    font-size: 0.9rem;
    transition: all 0.2s ease;

    .v-icon {
      opacity: 0.6;
    }

    &:hover {
      color: #00B0FF;
      transform: translateX(4px);
    }

    &.abuse-link {
      color: rgba(255, 167, 38, 0.85);
      margin-top: 4px;

      .v-icon {
        color: rgba(255, 167, 38, 0.7);
      }

      &:hover {
        color: #ffa726;
      }
    }
  }
}

/* Bottom bar */
.footer-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 24px;
  border-top: 1px solid rgba(120, 163, 255, 0.1);
  gap: 24px;
}

.copyright {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.7);
}

.secondary-links {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;

  a {
    color: rgba(200, 218, 255, 0.6);
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: #00B0FF;
    }
  }

  .separator {
    color: rgba(200, 218, 255, 0.3);
  }
}

.social-links {
  display: flex;
  align-items: center;
  gap: 8px;
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(120, 163, 255, 0.1);
  color: rgba(200, 218, 255, 0.7);
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 176, 255, 0.1);
    border-color: rgba(0, 176, 255, 0.3);
    color: #00B0FF;
    transform: translateY(-2px);
  }
}

.legal-note {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.75);
}

/* Responsive */
@media (max-width: 900px) {
  .footer-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 40px 32px;
  }

  .footer-brand {
    grid-column: span 2;
    padding-right: 0;
  }

  .brand-tagline {
    max-width: 100%;
  }

  .contact-items {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 16px 32px;
  }
}

@media (max-width: 600px) {
  .footer-inner {
    padding: 40px 16px 24px;
  }

  .footer-grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .footer-brand {
    grid-column: span 1;
    text-align: center;
  }

  .brand-logo {
    justify-content: center;
  }

  .brand-tagline {
    margin-left: auto;
    margin-right: auto;
  }

  .contact-items {
    justify-content: center;
  }

  .footer-col {
    text-align: center;
  }

  .col-title::after {
    left: 50%;
    transform: translateX(-50%);
  }

  .col-links a {
    justify-content: center;

    &:hover {
      transform: none;
    }
  }

  .footer-bottom {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }

  .social-links {
    order: -1;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .contact-item,
  .col-links a,
  .secondary-links a,
  .social-link {
    transition: none !important;
  }
}
</style>
